import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  Alert,
  type AlertColor,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { type CronListItem, readCronTriggerError, useTriggerCronMutation } from '../api/cronApi';
import CronRunHistory from './CronRunHistory';

interface CronJobCardProps {
  cron: CronListItem;
}

const statusColors: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  queued: 'warning',
  published: 'success',
  failed: 'error',
  running: 'warning',
  skipped: 'default',
};

export default function CronJobCard({ cron }: CronJobCardProps) {
  const [triggerCron, { isLoading: isTriggering }] = useTriggerCronMutation();
  const [triggerResult, setTriggerResult] = useState<{
    type: AlertColor;
    message: string;
  } | null>(null);

  const activeRun = cron.active_run;

  const handleTrigger = async () => {
    setTriggerResult(null);

    try {
      await triggerCron({ name: cron.name }).unwrap();
      setTriggerResult({
        type: 'success',
        message: `Triggered "${cron.name}" successfully.`,
      });
    } catch (err) {
      const details = readCronTriggerError(err);
      const activeRunStartedAt = details.active_run?.started_at
        ? formatDistanceToNow(new Date(details.active_run.started_at), { addSuffix: true })
        : 'an unknown time';
      if (details.error === 'already_running' && details.active_run) {
        setTriggerResult({
          type: 'info',
          message: `"${cron.name}" is already ${details.active_run.status} from a ${details.active_run.trigger_type} trigger started ${activeRunStartedAt}.`,
        });
        return;
      }

      setTriggerResult({
        type: 'error',
        message: details.error_description || 'Failed to trigger cron job.',
      });
    }
  };

  const nextRunText = cron.next_run
    ? formatDistanceToNow(new Date(cron.next_run), { addSuffix: true })
    : 'Unknown';

  const lastRunText = cron.last_run
    ? formatDistanceToNow(new Date(cron.last_run.started_at), { addSuffix: true })
    : 'Never';
  const activeRunText = activeRun
    ? formatDistanceToNow(new Date(activeRun.started_at), { addSuffix: true })
    : null;

  return (
    <Card variant="outlined">
      <CardContent>
        {triggerResult && (
          <Alert
            severity={triggerResult.type}
            sx={{ mb: 2 }}
            onClose={() => setTriggerResult(null)}
          >
            {triggerResult.message}
          </Alert>
        )}

        {/* Header */}
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
        >
          <Box>
            <Typography variant="h6" component="h3" sx={{ fontFamily: 'monospace' }}>
              {cron.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cron.description}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={
              isTriggering ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />
            }
            onClick={handleTrigger}
            disabled={isTriggering}
          >
            Trigger
          </Button>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Details grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Schedule:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {cron.cron}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Topic:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {cron.topic}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Next run:
          </Typography>
          <Typography variant="body2">{nextRunText}</Typography>

          <Typography variant="body2" color="text.secondary">
            Last run:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{lastRunText}</Typography>
            {cron.last_run && (
              <Chip
                size="small"
                label={cron.last_run.status}
                color={statusColors[cron.last_run.status] || 'default'}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            Active:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {activeRun ? (
              <>
                <Chip
                  size="small"
                  label={activeRun.status}
                  color={statusColors[activeRun.status] || 'default'}
                />
                <Chip size="small" label={activeRun.trigger_type} variant="outlined" />
                <Typography variant="body2">{activeRunText}</Typography>
              </>
            ) : (
              <Typography variant="body2">Idle</Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Run history */}
        <CronRunHistory scheduleName={cron.name} />
      </CardContent>
    </Card>
  );
}
